import { QueryKey } from '@tanstack/react-query'
import { AddressTypes, SearchRequest } from '@/api/generated/model'

export const FIVE_MINUTES = 5 * 60 * 1000

export const isQueryKeySubset = (
  subset: QueryKey,
  superset: QueryKey
): boolean => {
  return subset.every((val, index) => val === superset[index])
}

export const queryKeys = {
  agreements: {
    detail: (modelKey?: string) => ['agreements', modelKey, 'detail'],
    productLines: (userOrganizationID: string, partnerID: string) => [
      'agreements',
      'product-lines',
      userOrganizationID,
      partnerID
    ],
    agreementItems: (modelKey: string) => [
      'agreements',
      modelKey,
      'agreementItems'
    ],
    products: {
      pricing: (agreementId: string, productId?: string) => [
        'agreements',
        agreementId,
        'products',
        'pricing',
        productId
      ]
    },
    categories: {
      byId: (agreementId: string, categoryId?: string) => [
        'agreements',
        agreementId,
        'categories',
        categoryId
      ]
    },
    branchLocations: {
      root: (agreementID: string) => [
        'agreements',
        agreementID,
        'agreementItems',
        'branchLocations'
      ],
      mappableToCustomer: (agreementID: string, customerID: string) => [
        'agreements',
        agreementID,
        'agreementItems',
        'branchLocations',
        'mappableToCustomer',
        customerID
      ],
      search: (
        agreementID: string,
        offset: number,
        sort: string,
        filterModel: any
      ) => [
        'agreements',
        agreementID,
        'agreementItems',
        'branchLocations',
        offset,
        sort,
        filterModel
      ]
    },
    customers: {
      root: (agreementID: string) => [
        'agreements',
        agreementID,
        'agreementItems',
        'customers'
      ],
      search: (
        agreementID: string,
        page: number,
        limit: number,
        sort: unknown,
        filterModel: unknown
      ) => [
        'agreements',
        agreementID,
        'agreementItems',
        'customers',
        page,
        limit,
        sort,
        filterModel
      ]
    }
  },
  analytics: {
    agreements: () => ['analytics', 'agreements'],
    unpricedAgreementItems: (orgId: string) => [
      'analytics',
      'unpriced-agreement-items',
      orgId
    ],
    manufacturerPartners: (distributorId: string) => [
      'analytics',
      'partners',
      distributorId
    ],
    recentCustomerAgreementsForManufacturerPartner: (
      manufacturerPartnerId: string,
      distributorId: string,
      topNAgreements: number
    ) => [
      'analytics',
      'agreements',
      'recent',
      manufacturerPartnerId,
      distributorId,
      topNAgreements
    ],
    recentPricesheetsForManufacturerPartner: (
      manufacturerPartnerId: string,
      distributorId: string
    ) => [
      'analytics',
      'pricesheets',
      'recent',
      manufacturerPartnerId,
      distributorId
    ],
    spaImpactRecords: (
      currentQuoteNumber: string,
      currentPricesheetId: string,
      futureQuoteNumber?: string,
      futurePricesheetId?: string
    ) => [
      'analytics',
      'spa',
      'impact',
      'records',
      currentQuoteNumber,
      currentPricesheetId,
      futureQuoteNumber,
      futurePricesheetId
    ],
    priceSheetComparison: {
      sheets: (orgId: string, mfgId: string) => [
        'analytics',
        'ps-comparison',
        'sheets',
        orgId,
        mfgId
      ],
      compare: (orgId: string, mfgId: string, oldId: string, newId: string) => [
        'analytics',
        'ps-comparison',
        'compare',
        orgId,
        mfgId,
        oldId,
        newId
      ],
      categoryRows: (
        orgId: string,
        mfgId: string,
        oldId: string,
        newId: string,
        category: string
      ) => [
        'analytics',
        'ps-comparison',
        'category-rows',
        orgId,
        mfgId,
        oldId,
        newId,
        category
      ]
    },
    claimRejection: {
      rejections: (orgId: string, mfgId: string) => [
        'analytics',
        'claim-rejection',
        'rejections',
        orgId,
        mfgId
      ]
    },
    spaPairComparison: {
      customers: (orgId: string, mfgId: string) => [
        'analytics',
        'spa-pair-comparison',
        'customers',
        orgId,
        mfgId
      ],
      agreements: (orgId: string, mfgId: string, customerId: string) => [
        'analytics',
        'spa-pair-comparison',
        'agreements',
        orgId,
        mfgId,
        customerId
      ],
      // Keyed on the agreement alone, not on the pair: the page diffs in the
      // browser, so exploring every pairing of a customer's agreements re-reads
      // these entries instead of refetching per pair.
      items: (orgId: string, mfgId: string, agreementId: string) => [
        'analytics',
        'spa-pair-comparison',
        'items',
        orgId,
        mfgId,
        agreementId
      ]
    },
    spaRevisionComparison: {
      quotes: (orgId: string, mfgId: string, days: number) => [
        'analytics',
        'spa-revision',
        'quotes',
        orgId,
        mfgId,
        days
      ],
      compare: (
        orgId: string,
        mfgId: string,
        days: number,
        quote: string | null
      ) => [
        'analytics',
        'spa-revision',
        'compare',
        orgId,
        mfgId,
        days,
        quote ?? ''
      ],
      quoteRows: (
        orgId: string,
        mfgId: string,
        days: number,
        quote: string
      ) => [
        'analytics',
        'spa-revision',
        'quote-rows',
        orgId,
        mfgId,
        days,
        quote
      ]
    }
  },
  claims: {
    attachments: (claimId: string) => ['claims', claimId, 'attachments'],
    history: (claimId: string) => ['claims', claimId, 'history'],
    id: (claimID?: string) => ['claims', claimID]
  },
  organizations: {
    branches: (organizationId: string, addressType: AddressTypes) => [
      'organizations',
      organizationId,
      'branches',
      addressType.toString()
    ],
    connections: (organizationId: string) => [
      'organizations',
      organizationId,
      'connections'
    ],
    organization: (organizationId?: string) => [
      'organizations',
      organizationId
    ],
    organizations: () => ['organizations'],
    partners: (organizationID?: string) => [
      'organizations',
      'partners',
      organizationID
    ],
    users: (organizationId?: string) => [
      'oragnizations',
      'users',
      organizationId
    ]
  },
  permissions: {
    allUserPermissions: () => ['permissions', 'all', 'user'],
    organizationRoles: (organizationID?: string) => [
      'org-permission-roles',
      organizationID
    ]
  },
  priceSheets: {
    id: (id?: string) => ['pricesheets', id]
  },
  productAliases: {
    // The pending-alias groups (one per ingestion batch + the manual bucket). The alias rows
    // within a group are served straight to AG Grid's server-side datasource, not React Query.
    groups: (manufacturerId?: string) => [
      'product-aliases',
      'groups',
      manufacturerId
    ]
  },
  productPricing: {
    id: (id?: string) => ['productPricing', id]
  },
  regions: {
    search: (request?: SearchRequest) => ['regions', 'search', request]
  },
  tasks: {
    all: () => ['tasks'],
    id: (id: string) => ['tasks', id]
  },
  users: {
    activity: (onlyRecentRecords: boolean | undefined) => [
      'users',
      'current',
      'activity',
      onlyRecentRecords ? 'recent' : 'all'
    ],
    // Keyed by the organization the account is operating as — a Super Admin
    // can switch organizations, and a fixed key would serve the previous
    // organization's cached entry alongside the new account.
    organization: (organizationID?: string) => [
      'users',
      'current',
      'organization',
      organizationID
    ],
    session: () => ['users', 'current', 'session']
  },
  erp: {
    config: (organizationID?: string) =>
      ['erp', organizationID, 'config'] as const,
    history: (organizationID?: string) =>
      ['erp', organizationID, 'history'] as const
  }
}
